import React from 'react';
import { TaskItem } from '../../types/tasks';
import { hasConflict, isCritical, isLate, isScheduled, rangeLabel } from '../../utils/projectPlan';
import { isMaterial, isRanked, materialStatus, materialTotal, money, num, valueLabel, valueOf } from '../../utils/projectValue';
import TaskIcon from '../tasks/TaskIcon';

/**
 * A project topic as chips — a material's quantity, price and status; a
 * task's value; when it's planned for, and whether it's critical or late —
 * for list rows and map topics alike. Empty for ordinary list items.
 */
export function planChips(item: TaskItem, costs?: Map<number, number>): React.ReactNode[] {
  const chips: React.ReactNode[] = [];
  if (isMaterial(item)) {
    const total = materialTotal(item);
    const status = materialStatus(item);
    chips.push(
      <span key="mat" className={`tk-chip tp-chip-mat is-${status.toLowerCase()}`} title={item.vendor ? `From ${item.vendor}` : 'To buy'}>
        <TaskIcon name="cart" size={11} />
        {status}
        {item.qty != null && item.qty !== 1 && ` · ${num(item.qty)}×`}
        {total !== null && ` · ${money(total)}`}
        {item.vendor && ` · ${item.vendor}`}
      </span>
    );
  } else if (costs) {
    const v = valueOf(item, costs);
    if (isRanked(v)) {
      chips.push(
        <span key="value" className="tk-chip tp-chip-value" title="Task value — what it gains for what it costs">
          <TaskIcon name="value" size={11} />
          {valueLabel(v)}
        </span>
      );
    }
  }
  if (item.progress >= 100) return chips;
  if (isScheduled(item)) {
    const milestone = item.node_kind === 'milestone';
    chips.push(
      <span key="plan" className="tk-chip tk-chip-muted" title={milestone ? 'Planned for' : 'Planned'}>
        <TaskIcon name={milestone ? 'milestone' : 'gantt'} size={11} />
        {rangeLabel(item.plan_start!, item.plan_finish!)}
      </span>
    );
  }
  if (hasConflict(item)) {
    chips.push(
      <span key="conflict" className="tk-chip tp-chip-conflict" title="Its start is pinned before what it waits for is done — move one of them">
        <TaskIcon name="critical" size={11} />
        Conflict · {item.conflict_days}d early
      </span>
    );
  }
  if (isLate(item)) {
    chips.push(
      <span key="late" className="tk-chip tk-chip-danger" title="As planned it can't be done in time for what's waiting on it">
        <TaskIcon name="critical" size={11} />
        {-item.slack_days!}d late
      </span>
    );
  }
  if (isCritical(item)) {
    chips.push(
      <span key="crit" className="tk-chip tp-chip-critical" title="On the critical path — any slip moves the finish">
        <TaskIcon name="critical" size={11} />
        Critical
      </span>
    );
  }
  const waits = (item.predecessors || []).length;
  if (waits > 0) {
    chips.push(
      <span key="waits" className="tk-chip tk-chip-muted" title={`Waits for ${waits} other task${waits === 1 ? '' : 's'}`}>
        <TaskIcon name="waits" size={11} />
        {waits}
      </span>
    );
  }
  return chips;
}

/** Small icon marking a milestone or an event, beside the row's other source icons. */
export function kindIcon(item: TaskItem): React.ReactNode {
  if (item.node_kind === 'material') {
    return <span key="kind" className="tk-src tp-src-material" title="Something to buy"><TaskIcon name="cart" size={13} /></span>;
  }
  if (item.node_kind === 'milestone') {
    return <span key="kind" className="tk-src tp-src-milestone" title="Milestone"><TaskIcon name="milestone" size={13} /></span>;
  }
  if (item.node_kind === 'event') {
    return <span key="kind" className="tk-src tp-src-event" title="Event"><TaskIcon name="event" size={13} /></span>;
  }
  return null;
}
